/**
 * Cliente HTTP do SPA. Fala SEMPRE com o BFF (same-origin), nunca com a
 * legacy-api diretamente — o token vive em cookie httpOnly no servidor.
 * Mutações enviam o token CSRF (double-submit) lido do cookie legível `fixe_csrf`.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly data?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

export const CSRF_COOKIE = 'fixe_csrf';
export const BFF_API_PREFIX = '/bff/api';
/** Namespace do BFF para a plataforma NPS (separado do legado /bff/api). */
export const BFF_NPS_PREFIX = '/bff/nps';

export function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

interface RequestOptions extends Omit<RequestInit, 'body' | 'method'> {
  body?: unknown;
}

async function request<T>(
  path: string,
  method: string,
  { body, headers, ...rest }: RequestOptions = {},
): Promise<T> {
  const isMutation = method !== 'GET' && method !== 'HEAD';
  const csrf = isMutation ? readCookie(CSRF_COOKIE) : null;

  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    ...rest,
  });

  if (!response.ok) {
    let data: unknown;
    try {
      data = await response.json();
    } catch {
      /* corpo não-JSON */
    }
    throw new ApiError(response.status, extractErrorMessage(data, response.statusText), data);
  }

  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get('content-type') ?? '';
  return (contentType.includes('application/json') ? await response.json() : await response.text()) as T;
}

function extractErrorMessage(data: unknown, fallback?: string): string {
  if (!data || typeof data !== 'object') return fallback || 'Erro inesperado';
  const raw = (data as { message?: unknown; error?: unknown }).message;
  if (typeof raw === 'string' && raw.trim()) return raw;
  if (raw && typeof raw === 'object') {
    const nested = (raw as { message?: unknown }).message;
    if (typeof nested === 'string' && nested.trim()) return nested;
  }
  const error = (data as { error?: unknown }).error;
  if (typeof error === 'string' && error.trim()) return error;
  return fallback || 'Erro inesperado';
}

export const http = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, 'GET', options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, 'POST', { ...options, body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, 'PUT', { ...options, body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, 'PATCH', { ...options, body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>(path, 'DELETE', options),
};

/** Upload multipart via rota dedicada do BFF (legacy-api exige FormData). */
export async function bffUpload<T>(path: string, formData: FormData): Promise<T> {
  const csrf = readCookie(CSRF_COOKIE);
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: csrf ? { 'X-CSRF-Token': csrf } : {},
    body: formData,
  });
  if (!response.ok) {
    let data: unknown;
    try {
      data = await response.json();
    } catch {
      /* corpo não-JSON */
    }
    throw new ApiError(response.status, extractErrorMessage(data, response.statusText), data);
  }
  return (await response.json()) as T;
}

/** Atalhos para a legacy-api via proxy autenticado do BFF (`/bff/api/...`). */
export const legacy = {
  get: <T>(path: string, options?: RequestOptions) => http.get<T>(`${BFF_API_PREFIX}${path}`, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    http.post<T>(`${BFF_API_PREFIX}${path}`, body, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    http.put<T>(`${BFF_API_PREFIX}${path}`, body, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    http.patch<T>(`${BFF_API_PREFIX}${path}`, body, options),
  delete: <T>(path: string, options?: RequestOptions) => http.delete<T>(`${BFF_API_PREFIX}${path}`, options),
};

/** Atalhos para a API NPS via BFF (`/bff/nps/...`). */
export const nps = {
  get: <T>(path: string, options?: RequestOptions) => http.get<T>(`${BFF_NPS_PREFIX}${path}`, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    http.post<T>(`${BFF_NPS_PREFIX}${path}`, body, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    http.put<T>(`${BFF_NPS_PREFIX}${path}`, body, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    http.patch<T>(`${BFF_NPS_PREFIX}${path}`, body, options),
  delete: <T>(path: string, options?: RequestOptions) => http.delete<T>(`${BFF_NPS_PREFIX}${path}`, options),
};
